/**
 * Sessao no navegador, igual pros dois fronts. O access token fica SO em memoria (nunca localStorage);
 * a sessao longa e o cookie HttpOnly do backend, renovado em /renovar. Nada aqui depende de estado local
 * do navegador pra funcionar: um link de e-mail aberto em outro aparelho funciona do mesmo jeito.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

export class ErroApi extends Error {
  /** `codigo`: o do cabecalho `x-codigo` da resposta; com ele, o suporte acha o pedido nos logs. */
  constructor(public status: number, public corpo: { erro?: string; dica?: string; [k: string]: unknown }, public codigo?: string) {
    super(corpo?.erro ?? `HTTP ${status}`);
  }
}
const codigoDe = (r: Response) => r.headers.get("x-codigo") ?? undefined;

export type RotasSessao = { renovar: string; sair: string; eu: string };
export type RespostaDeEntrada = { access_token: string; expira_em: string };

type Estado<U> = { status: "carregando" } | { status: "anonimo" } | { status: "logado"; usuario: U };
type Valor<U> = {
  estado: Estado<U>;
  /** Chamada autenticada; renova sozinha uma vez se o token venceu. */
  api: <T>(caminho: string, init?: RequestInit) => Promise<T>;
  /** Envio de arquivo (multipart), autenticado. Sem Content-Type: o navegador monta o boundary. */
  enviar: <T>(caminho: string, form: FormData) => Promise<T>;
  /** Arquivo protegido (comprovante) como Blob, pra mostrar na tela ou abrir numa aba. */
  arquivo: (caminho: string) => Promise<Blob>;
  /** Chamada sem token (entrar, recuperar), que pode receber o cookie de sessao. */
  publica: <T>(caminho: string, corpo: unknown) => Promise<T>;
  /** Depois de uma rota que devolve sessao (entrar, definir senha, recuperar). */
  entrou: (r: RespostaDeEntrada) => Promise<void>;
  sair: () => Promise<void>;
};

export function criarSessao<U>(rotas: RotasSessao) {
  const Ctx = createContext<Valor<U> | null>(null);

  /**
   * `tokenInicial`: sessao assistida (spec 012). O token vem no fragmento do link, vale sozinho (sem cookie) e nao
   * renova: quando vence ou e encerrado, a area volta pro anonimo e explica.
   */
  function ProvedorSessao({ apiUrl, tokenInicial, children }: { apiUrl: string; tokenInicial?: string | null; children: ReactNode }) {
    const [estado, setEstado] = useState<Estado<U>>({ status: "carregando" });
    const token = useRef<string | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Caminho relativo vai pra API do ambiente; endereco completo (ex.: o servico de pagamentos) vai como veio.
    const url = useCallback((c: string) => (/^https?:\/\//.test(c) ? c : `${apiUrl.replace(/\/$/, "")}${c}`), [apiUrl]);

    const agendar = useCallback((expiraEm: string, renovar: () => Promise<boolean>) => {
      if (timer.current) clearTimeout(timer.current);
      const falta = new Date(expiraEm).getTime() - Date.now() - 60_000;
      timer.current = setTimeout(() => void renovar(), Math.max(10_000, falta));
    }, []);

    /**
     * Uma renovacao por vez. O backend troca o cookie a cada renovacao, e cookie ja trocado que volta e tratado como
     * roubo: derruba TODAS as sessoes da pessoa. Duas renovacoes juntas (a da abertura da pagina e a de uma chamada que
     * voltou 401, ou varias consultas quando o token vence) deslogavam o usuario. Na mesma aba, quem chega depois espera
     * a mesma promessa; entre abas, a trava do navegador faz a segunda sair ja com o cookie novo.
     */
    const emCurso = useRef<Promise<boolean> | null>(null);
    const assistida = useRef<boolean>(Boolean(tokenInicial));
    if (tokenInicial && token.current === null) token.current = tokenInicial;
    const renovar = useCallback((): Promise<boolean> => {
      // Sessao assistida nao tem cookie nem renovacao: acabou o prazo, acabou.
      if (assistida.current) return Promise.resolve(false);
      if (emCurso.current) return emCurso.current;
      const pedir = async (): Promise<boolean> => {
        try {
          const r = await fetch(url(rotas.renovar), { method: "POST", credentials: "include" });
          if (!r.ok) return false;
          const j = (await r.json()) as RespostaDeEntrada;
          token.current = j.access_token;
          agendar(j.expira_em, renovar);
          return true;
        } catch {
          return false;
        }
      };
      const travas = typeof navigator !== "undefined" ? navigator.locks : undefined;
      // request() resolve com o valor do callback ja desembrulhado; a tipagem da lib nao desembrulha a promessa.
      const comTrava = travas ? (travas.request(`mtc-renovar:${rotas.renovar}`, pedir) as unknown as Promise<boolean>) : pedir();
      const p = comTrava.finally(() => {
        emCurso.current = null;
      });
      emCurso.current = p;
      return p;
    }, [url, agendar]);

    /**
     * Uma chamada com o token; se venceu, renova uma vez e tenta de novo. 401 no fim derruba a sessao, mas so da API
     * principal: 401 de outro servico (pagamentos mal configurado, por exemplo) vira erro na tela, nao logout.
     */
    const chamar = useCallback(async (caminho: string, init: RequestInit, json: boolean): Promise<Response> => {
      const tentar = () => fetch(url(caminho), { ...init, credentials: "include", headers: { ...(json ? { "Content-Type": "application/json" } : {}), ...(init.headers ?? {}), ...(token.current ? { Authorization: `Bearer ${token.current}` } : {}) } });
      let r = await tentar();
      if (r.status === 401 && (await renovar())) r = await tentar();
      if (r.status === 401 && !/^https?:\/\//.test(caminho)) { token.current = null; setEstado({ status: "anonimo" }); }
      return r;
    }, [url, renovar]);

    const api = useCallback(async <T,>(caminho: string, init: RequestInit = {}): Promise<T> => {
      const r = await chamar(caminho, init, true);
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new ErroApi(r.status, corpo, codigoDe(r));
      return corpo as T;
    }, [chamar]);

    const enviar = useCallback(async <T,>(caminho: string, form: FormData): Promise<T> => {
      const r = await chamar(caminho, { method: "POST", body: form }, false);
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new ErroApi(r.status, corpo, codigoDe(r));
      return corpo as T;
    }, [chamar]);

    const arquivo = useCallback(async (caminho: string): Promise<Blob> => {
      const r = await chamar(caminho, {}, false);
      if (!r.ok) throw new ErroApi(r.status, await r.json().catch(() => ({})), codigoDe(r));
      return r.blob();
    }, [chamar]);

    const publica = useCallback(async <T,>(caminho: string, corpo: unknown): Promise<T> => {
      const r = await fetch(url(caminho), { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(corpo) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new ErroApi(r.status, j, codigoDe(r));
      return j as T;
    }, [url]);

    const carregarUsuario = useCallback(async () => {
      try {
        const usuario = await api<U>(rotas.eu);
        setEstado({ status: "logado", usuario });
      } catch {
        setEstado({ status: "anonimo" });
      }
    }, [api]);

    const entrou = useCallback(async (r: RespostaDeEntrada) => {
      token.current = r.access_token;
      agendar(r.expira_em, renovar);
      await carregarUsuario();
    }, [agendar, renovar, carregarUsuario]);

    const sair = useCallback(async () => {
      await fetch(url(rotas.sair), { method: "POST", credentials: "include" }).catch(() => undefined);
      if (timer.current) clearTimeout(timer.current);
      token.current = null;
      setEstado({ status: "anonimo" });
    }, [url]);

    useEffect(() => {
      void (async () => (assistida.current || (await renovar()) ? carregarUsuario() : setEstado({ status: "anonimo" })))();
      return () => { if (timer.current) clearTimeout(timer.current); };
    }, [renovar, carregarUsuario]);

    const valor = useMemo(() => ({ estado, api, enviar, arquivo, publica, entrou, sair }), [estado, api, enviar, arquivo, publica, entrou, sair]);
    return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
  }

  function useSessao(): Valor<U> {
    const v = useContext(Ctx);
    if (!v) throw new Error("useSessao fora do ProvedorSessao");
    return v;
  }

  return { ProvedorSessao, useSessao };
}

/**
 * Mensagem pra tela a partir de um erro da API. O texto do backend (`erro`) sempre vence, ate num 5xx (ex.: "A Nelogica
 * nao respondeu"): frase generica so quando ele nao disse nada (G07, 4.5).
 */
export function mensagemDe(err: unknown, reserva = "Algo deu errado. Tente de novo em instantes."): string {
  if (err instanceof ErroApi) {
    // Validacao do Fastify/Zod vem sem `erro`: { code: "FST_ERR_VALIDATION", message: "body/campo o que falta" }.
    const validacao = err.corpo?.code === "FST_ERR_VALIDATION" && typeof err.corpo.message === "string" ? err.corpo.message.replace(/^(body|querystring|params)(\/\S*)?\s/, "") : undefined;
    const texto = err.corpo?.erro ?? validacao;
    // Erro do nosso lado (5xx): o codigo vai junto, pra quem ve mandar pro suporte.
    const codigo = err.status >= 500 && err.codigo ? ` Código do erro: ${err.codigo}.` : "";
    if (texto) return primeiraMaiuscula(texto) + codigo;
    if (err.status === 429) return "Muitas tentativas. Espere um pouco e tente de novo.";
    return reserva + codigo;
  }
  return "Não foi possível falar com o servidor. Confira sua conexão.";
}
const primeiraMaiuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1) + (/[.!?]$/.test(s) ? "" : ".");
